import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, onUnmounted, ref } from 'vue';
import { AxiosError, isCancel } from 'axios';

import { useLoaderStore } from '@/stores/useLoaderStore';
import { fetchPostJobBookXls } from '@/helpers/fetchers';
import { SpreadsheetBooking400Error } from '@/helpers/throwables';

export interface SpreadsheetJobBookingViewDataComposable {
  isFetchingSpreadsheetJobBookingData: Readonly<Ref<boolean>>;

  spreadsheetJobBookingData: Readonly<
    Ref<ReadonlyDeep<openapi.PostJobBookXls_201> | undefined>
  >;

  spreadsheetJobBookingDataError: Readonly<
    Ref<ReadonlyDeep<openapi.PostJobBookXls_400> | undefined>
  >;

  refreshSpreadsheetJobBookingData: (file: File) => Promise<void>;

  reload: () => SpreadsheetJobBookingViewDataComposable;
}

export default function useSpreadsheetJobBookingViewData(): SpreadsheetJobBookingViewDataComposable {
  const loaderStore = useLoaderStore();

  let spreadsheetJobBookingDataController: AbortController | undefined;

  const spreadsheetJobBookingDataFetchCounter = ref(0);

  const isFetchingSpreadsheetJobBookingData = computed(
    () => !!spreadsheetJobBookingDataFetchCounter.value,
  );

  const spreadsheetJobBookingData =
    ref<ReadonlyDeep<openapi.PostJobBookXls_201>>();

  const spreadsheetJobBookingDataError =
    ref<ReadonlyDeep<openapi.PostJobBookXls_400>>();

  function clear(): void {
    if (spreadsheetJobBookingDataController) {
      spreadsheetJobBookingDataController.abort();
    }
  }

  onUnmounted(() => {
    clear();
  });

  async function _fetchSpreadsheetJobBookingData(
    file: File,
  ): Promise<ReadonlyDeep<openapi.PostJobBookXls_201> | undefined> {
    if (spreadsheetJobBookingDataController) {
      spreadsheetJobBookingDataController.abort();
    }
    spreadsheetJobBookingDataController = new AbortController();
    return await fetchPostJobBookXls({}, file, {
      signal: spreadsheetJobBookingDataController.signal,
    })
      .then(function (data) {
        spreadsheetJobBookingDataError.value = undefined;
        return data;
      })
      .catch(function (err: unknown) {
        if (isCancel(err)) return undefined;
        if (err instanceof SpreadsheetBooking400Error) {
          const cause = err.cause as AxiosError;
          spreadsheetJobBookingDataError.value = cause.response
            ?.data as ReadonlyDeep<openapi.PostJobBookXls_400>;
          return undefined;
        }
        throw err;
      });
  }

  async function refreshSpreadsheetJobBookingData(file: File): Promise<void> {
    loaderStore.increaseGlobalLoader();
    spreadsheetJobBookingDataFetchCounter.value++;
    spreadsheetJobBookingData.value = await _fetchSpreadsheetJobBookingData(
      file,
    ).finally(function () {
      spreadsheetJobBookingDataFetchCounter.value--;
      loaderStore.decreaseGlobalLoader();
    });
  }

  function reload(): SpreadsheetJobBookingViewDataComposable {
    // void refreshSpreadsheetJobBookingData(file); // there is no file here, so it cannot be reloaded
    return {
      isFetchingSpreadsheetJobBookingData,
      spreadsheetJobBookingData,
      spreadsheetJobBookingDataError,
      refreshSpreadsheetJobBookingData,
      reload,
    };
  }

  return {
    isFetchingSpreadsheetJobBookingData,
    spreadsheetJobBookingData,
    spreadsheetJobBookingDataError,
    refreshSpreadsheetJobBookingData,
    reload,
  };
}
